import React from 'react';
import { fetchLookupInputOptions } from '../../../services/manualDashboardService';
import type { SelectOption } from '../../../ui-kit';
import type { LookupInputDef } from '../../../types/lookupDashboard';
import { countLabel } from './lookupFormat';

/**
 * Suggestions for one input as its search is typed.
 *
 * Every value on record is offered. Given the inputs chosen before this one,
 * the values on record with them come first under the spec's `paired_label`
 * — "Flown from KTEB" — and everything else follows. The first input, with
 * nothing before it, is simply most-used first.
 *
 * Nothing is fetched until the list is first opened — `activate` — so a
 * board with two inputs does not cost two requests just to load.
 */
export function useInputOptions({
  dashboardId,
  input,
  otherInputs,
  noun,
}: {
  dashboardId?: string;
  input: LookupInputDef;
  otherInputs: Record<string, string>;
  /** What a count counts, for "13 legs". */
  noun: string;
}) {
  const [active, setActive] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [options, setOptions] = React.useState<SelectOption[]>([]);
  const [loading, setLoading] = React.useState(false);
  const others = JSON.stringify(otherInputs);

  React.useEffect(() => {
    if (!active || !dashboardId) return undefined;
    const controller = new AbortController();
    const pairedWith = JSON.parse(others) as Record<string, string>;
    const paired = Object.keys(pairedWith).length > 0;
    // "Flown to {destination}" with the destination's value filled in.
    const pairedGroup = (input.paired_label ?? 'On record with {others}').replace(
      /\{(\w+)\}/g,
      (whole, key: string) => pairedWith[key] ?? (key === 'others' ? Object.values(pairedWith).join(', ') : whole),
    );
    const otherGroup = `Other ${input.label.toLowerCase()}s`;

    setLoading(true);
    const timer = window.setTimeout(() => {
      fetchLookupInputOptions(
        dashboardId,
        input.key,
        { search: search.trim() || undefined, inputs: pairedWith },
        controller.signal,
      )
        .then((data) => {
          const anyPaired = data.options.some((o) => o.pair_count > 0);
          setOptions(
            data.options.map((o) => {
              const withOthers = paired && o.pair_count > 0;
              return {
                value: o.value,
                label: o.name ? `${o.value} · ${o.name}` : o.value,
                description: o.place,
                meta: countLabel(withOthers ? o.pair_count : o.count, noun),
                // Grouped only when there is something to set apart.
                group: paired && anyPaired ? (withOthers ? pairedGroup : otherGroup) : undefined,
              };
            }),
          );
        })
        .catch(() => undefined)
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, 200);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [active, dashboardId, input.key, input.label, input.paired_label, search, others, noun]);

  return { options, loading, setSearch, activate: () => setActive(true) };
}
